import { reactive } from "vue";

import { apiBaseUrl, isSameOriginApi } from "../utils/clientHost";

/**
 * Connection state shared with the UI. When the bench is not reachable the app
 * falls back to sample data and shows a "Sample data" chip in the top bar.
 */
export const connection = reactive({
	online: true,
	checked: false,
	lastError: "",
});

function csrfToken() {
	return (
		localStorage.getItem("csrfToken") ||
		window.csrf_token ||
		(window.frappe_boot && window.frappe_boot.csrf_token) ||
		""
	);
}

function authHeaders({ omitCsrf = false } = {}) {
	const headers = {
		"Content-Type": "application/json",
		Accept: "application/json",
	};
	const token = csrfToken();
	if (token && !omitCsrf) headers["X-Frappe-CSRF-Token"] = token;
	if (isSameOriginApi()) {
		const clientCode = localStorage.getItem("clientCode");
		if (clientCode) headers["X-Client-Code"] = clientCode.trim().toLowerCase();
	}
	return headers;
}

export class ApiError extends Error {
	constructor(message, status, payload) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.payload = payload;
	}
}

/**
 * Call a Frappe method on the configured ERPNext site.
 * Returns the parsed JSON body (`{ message }`), not just `message`.
 */
export async function request(method, args = {}, options = {}) {
	const { httpMethod = "POST", skipAuthRedirect = false, skipCsrfRetry = false } = options;
	const isGet = httpMethod === "GET";
	const query = isGet ? `?${new URLSearchParams(serialise(args))}` : "";
	const base = apiBaseUrl();

	let response;
	try {
		response = await fetch(`${base}/api/method/${method}${query}`, {
			method: httpMethod,
			credentials: "include",
				headers: authHeaders({ omitCsrf: method === "login" }),
			body: isGet ? undefined : JSON.stringify(serialise(args)),
		});
	} catch {
		throw new ApiError("Cannot reach Trip Planner. Check the connection and try again.", 0);
	}

	const payload = await response.json().catch(() => ({}));

	const csrfFailed =
		payload?.exc_type === "CSRFTokenError" ||
		/csrf|invalid request/i.test(String(payload?.message || payload?.exc || ""));
	if ((response.status === 417 || csrfFailed) && !skipCsrfRetry && !options._retried) {
		await refreshCsrf();
		return request(method, args, { ...options, _retried: true });
	}

	if (!response.ok) {
		const message = extractError(payload) || response.statusText || "Request failed";
		if (response.status === 401 && !skipAuthRedirect) redirectToLogin();
		throw new ApiError(message, response.status, payload);
	}

	connection.online = true;
	connection.checked = true;
	connection.lastError = "";
	return payload;
}

export async function refreshCsrf() {
		const payload = await request("cmenu.api.get_new_csrf_token", {}, {
		httpMethod: "GET",
		skipAuthRedirect: true,
		skipCsrfRetry: true,
	});
	const token = payload?.message?.csrf_token || (typeof payload?.message === "string" ? payload.message : "");
	if (token) localStorage.setItem("csrfToken", token);
	return token;
}

/** Call a whitelisted Frappe method and return `message`. */
export async function call(method, args = {}, options = {}) {
	try {
		const payload = await request(method, args, options);
		return payload.message;
	} catch (error) {
		if (error instanceof ApiError) {
			connection.online = false;
			connection.lastError = error.message;
		}
		throw error;
	}
}

/** Attach a file to a Frappe document. Returns the File message. */
export async function uploadFile(doctype, docname, file, options = {}) {
	const send = async () => {
		const headers = {};
		const token = csrfToken();
		if (token) headers["X-Frappe-CSRF-Token"] = token;
		if (isSameOriginApi()) {
			const clientCode = localStorage.getItem("clientCode");
			if (clientCode) headers["X-Client-Code"] = clientCode.trim().toLowerCase();
		}
		const body = new FormData();
		const upload =
			options.filename && file?.name !== options.filename
				? new File([file], options.filename, { type: file.type || "application/octet-stream" })
				: file;
		body.append("file", upload);
		body.append("doctype", doctype);
		body.append("docname", docname);
		body.append("is_private", "1");
		if (options.filename) {
			// Frappe reads file_name / fname / filename depending on version.
			body.append("file_name", options.filename);
			body.append("filename", options.filename);
			body.append("fname", options.filename);
		}
		if (options.attachedToField) {
			body.append("attached_to_field", options.attachedToField);
		}
		return fetch(`${apiBaseUrl()}/api/method/upload_file`, {
			method: "POST",
			credentials: "include",
			headers,
			body,
		});
	};

	let response;
	try {
		response = await send();
	} catch {
		throw new ApiError("Cannot reach Trip Planner. Check the connection and try again.", 0);
	}

	let payload = await response.json().catch(() => ({}));
	if (response.status === 417 && payload?.exc_type === "CSRFTokenError") {
		await refreshCsrf();
		response = await send();
		payload = await response.json().catch(() => ({}));
	}

	if (!response.ok) {
		throw new ApiError(extractError(payload) || response.statusText || "Upload failed", response.status, payload);
	}

	connection.online = true;
	connection.checked = true;
	connection.lastError = "";
	return payload.message;
}

function serialise(args) {
	const output = {};

	for (const [key, value] of Object.entries(args)) {
		if (value === undefined || value === null || value === "") continue;
		output[key] = typeof value === "object" ? JSON.stringify(value) : value;
	}

	return output;
}

function extractError(payload) {
	if (!payload) return "";

	const messages = payload._server_messages ? safeParse(payload._server_messages) : null;
	if (Array.isArray(messages) && messages.length) {
		const first = safeParse(messages[0]);
		return (first && first.message) || messages[0];
	}

	if (payload.exc_type === "CSRFTokenError") return "Session expired. Try again.";

	if (typeof payload.message === "string" && payload.message && payload.message !== "Internal Server Error") {
		return payload.message;
	}

	if (typeof payload.exception === "string" && payload.exception && !payload.exception.includes("Traceback")) {
		return payload.exception.split("\n")[0];
	}

	return payload.exc || "Request failed";
}

function safeParse(value) {
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}

function redirectToLogin() {
	const path = window.location.pathname || "/";
	if (path === "/login" || path === "/register") return;
	const redirect = encodeURIComponent(path + window.location.search);
	window.location.assign(`/login?redirect=${redirect}`);
}

/**
 * Run a backend call and fall back to sample data when the bench is missing.
 * Keeps the UI reviewable before the app is installed on a site.
 */
export async function callWithFallback(method, args, fallback) {
	try {
		const result = await call(method, args);
		return { data: result, sample: false };
	} catch (error) {
		connection.online = false;
		connection.lastError = error.message;
		return { data: typeof fallback === "function" ? fallback(args) : fallback, sample: true };
	}
}
